/**
 * Shared scroll-to-first-error hook.
 *
 * Returns a callback that, when invoked with `@mantine/form`'s errors map
 * (Mantine passes it as the second arg of `form.onSubmit(success, failure)`),
 * finds the first errored input in DOM order, scrolls it into view, focuses
 * it, and announces the count of errored fields to screen readers.
 *
 * Why a shared hook?
 *   Every dialog and form that adopts `@mantine/form` per the
 *   `.cursor/rules/form-validation.mdc` convention should get the same
 *   "submit fails -> scroll to + focus the first bad field" behaviour with
 *   one line. Pulling it into a hook keeps the call sites trivial and gives
 *   us a single place to fix iOS Safari quirks, focus management, and a11y.
 *
 * How it finds the field:
 *   `getInputProps(path)` automatically stamps `data-path={path}` on every
 *   input it wires up (verified in `node_modules/@mantine/form/esm/use-form.mjs`),
 *   so we use `[data-path="…"]` as a stable, framework-supplied selector
 *   that works for every Mantine input (TextInput, Select, Autocomplete,
 *   DateInput, NumberInput, Textarea, etc.) without requiring per-component
 *   decoration. (`getInputProps` does NOT set the HTML `name` attribute, so
 *   that selector wouldn't work.)
 *
 * Wizard step jumping:
 *   When a field reported as errored is not currently mounted (e.g. it
 *   belongs to a non-active wizard step), the hook needs to switch to that
 *   step before it can scroll to and focus the field. Mantine's `<Stepper>`
 *   mounts/unmounts step content across React commits, so a single
 *   `requestAnimationFrame` after `onStepChange` often runs before the new
 *   step's content is in the DOM. Instead, the hook stores the pending
 *   target in a ref and a small `useEffect` watching `activeStep` performs
 *   the scroll once the new step has mounted.
 *
 * Accessibility:
 *   When a scroll fires, the hook also writes "N field(s) need attention" to
 *   a shared visually-hidden `aria-live="polite"` region (mounted once at
 *   the app root with id `ss-form-error-live`) so screen-reader users get a
 *   consistent announcement regardless of which dialog or form failed
 *   validation.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { UseFormReturnType } from '@mantine/form';

/** ID of the live region we expect at the app root for a11y announcements. */
const LIVE_REGION_ID = 'ss-form-error-live';

/** Selector base for `getInputProps`-wired inputs. */
const DATA_PATH_ATTR = 'data-path';

/**
 * Optional configuration for {@link useScrollToFirstError}.
 *
 * For simple single-step dialogs none of these are required; just call
 * `useScrollToFirstError(form)`. For wizards, pass `fieldStepMap`,
 * `activeStep`, and `onStepChange` so the hook can jump to the owning step.
 */
export interface UseScrollToFirstErrorOptions {
  /**
   * Optional map of form-field path to step index. If provided AND the first
   * errored field belongs to a different step than the one currently visible,
   * the hook calls `onStepChange(step)` and defers the scroll until that
   * step's effect fires. Required for wizards.
   */
  fieldStepMap?: Record<string, number>;

  /** Currently visible step. Required when `fieldStepMap` is provided. */
  activeStep?: number;

  /** Callback to switch the wizard to a specific step. Required when `fieldStepMap` is provided. */
  onStepChange?: (step: number) => void;

  /**
   * `scrollIntoView` behavior. Defaults to `'smooth'` for a less jarring
   * jump. Override to `'auto'` (instant) for tests or contexts where smooth
   * scrolling is undesirable.
   */
  behavior?: ScrollBehavior;
}

/**
 * Mantine's `errors` map shape. Keys are field paths (`getInputProps` paths),
 * values are the per-field error message (or `false`/`null` if cleared).
 */
type ErrorsMap = UseFormReturnType<unknown>['errors'];

/** Shape of the callback we expose. */
export type ScrollToFirstErrorFn = (errors?: ErrorsMap) => boolean;

/**
 * Returns a stable callback that scrolls to and focuses the first errored
 * field in `form.errors`. Pass it as the second arg of `form.onSubmit`:
 *
 * ```tsx
 * const scrollToFirstError = useScrollToFirstError(form);
 * const handleSubmit = form.onSubmit(handleSave, scrollToFirstError);
 * ```
 *
 * Returns `true` if a target was scrolled-to (or queued for a step jump),
 * `false` if the errors map was empty or no candidate was findable.
 */
export function useScrollToFirstError<T>(
  form: UseFormReturnType<T>,
  opts?: UseScrollToFirstErrorOptions
): ScrollToFirstErrorFn {
  /**
   * When the first errored field is on a non-active wizard step, we stash
   * its path here and switch steps. The effect below picks it up once the
   * new step's content has mounted in the DOM.
   */
  const pendingTargetRef = useRef<string | null>(null);

  // Wizard: when activeStep changes AND we have a pending target, scroll it
  // now. Using an effect (not requestAnimationFrame) because Mantine's
  // <Stepper> mounts step content across React commits, and rAF can fire
  // before the new step is in the DOM.
  useEffect(() => {
    if (!pendingTargetRef.current) return;
    const target = pendingTargetRef.current;
    pendingTargetRef.current = null;
    scrollAndFocus(target, opts?.behavior);
  }, [opts?.activeStep, opts?.behavior]);

  return useCallback<ScrollToFirstErrorFn>(
    (errors) => {
      // Mantine calls the failure callback with the errors map; fall back to
      // the form's own errors state if the caller invokes us manually.
      const errorMap: ErrorsMap = errors ?? form.errors;
      const errorKeys = Object.keys(errorMap).filter((key) => {
        const value = (errorMap as Record<string, unknown>)[key];
        return value !== null && value !== undefined && value !== false;
      });
      if (errorKeys.length === 0) return false;

      // Pick the first errored field by document order. This naturally
      // matches what the user perceives as "first" rather than React object
      // key order (which can flip if the form schema changes).
      const firstByDom = pickFirstByDomOrder(errorKeys);

      if (!firstByDom) {
        // None of the errored fields are currently in the DOM. That almost
        // always means they belong to a non-active wizard step, so try to
        // jump to the owning step and queue the scroll for after mount.
        const firstKey = errorKeys[0]!;
        const step = opts?.fieldStepMap?.[firstKey];
        if (
          typeof step === 'number' &&
          step !== opts?.activeStep &&
          opts?.onStepChange
        ) {
          pendingTargetRef.current = firstKey;
          opts.onStepChange(step);
          announce(errorKeys.length);
          return true;
        }

        // No step jump possible; nothing else we can do.
        return false;
      }

      scrollAndFocus(firstByDom, opts?.behavior);
      announce(errorKeys.length);
      return true;
    },
    // form.errors is intentionally read inside the callback (we don't need
    // to recreate the callback when the errors map mutates).
    [form, opts?.fieldStepMap, opts?.activeStep, opts?.onStepChange, opts?.behavior]
  );
}

/**
 * Finds an input wired by `getInputProps(path)` and brings it into view +
 * focus.
 *
 * Focus is requested with `preventScroll: true` so the focus call doesn't
 * fight the smooth-scroll. Hidden / disabled / readOnly inputs are scrolled
 * to but not focused (focusing them is either a no-op or a confusing UX).
 */
function scrollAndFocus(path: string, behavior: ScrollBehavior = 'smooth'): void {
  const target = querySelectorByPath(path);
  if (!target) return;

  // `block: 'center'` keeps the target visually centred. Inside a Mantine
  // <Modal>, scrollIntoView walks up to the nearest scroll container (the
  // modal body), not the viewport, so this works for in-modal forms too.
  target.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });

  if (target instanceof HTMLElement && isFocusable(target)) {
    // preventScroll keeps the smooth scroll going; without it, Safari and
    // Firefox snap-jump to the focused element and cancel the smooth motion.
    target.focus({ preventScroll: true });
  }
}

/**
 * Returns the path whose `data-path` element appears earliest in document
 * order. Returns `null` if none of the candidate paths resolve to a DOM
 * element (e.g. the field is on a non-mounted wizard step).
 */
function pickFirstByDomOrder(paths: string[]): string | null {
  if (typeof document === 'undefined') return null;

  let bestPath: string | null = null;
  let bestEl: Element | null = null;

  for (const path of paths) {
    const el = querySelectorByPath(path);
    if (!el) continue;

    if (!bestEl) {
      bestEl = el;
      bestPath = path;
      continue;
    }

    // Node.compareDocumentPosition returns a bitmask; the FOLLOWING bit
    // (0x04) is set when `el` comes AFTER `bestEl`, so anything without it
    // means `el` comes BEFORE and is the new "first".
    const relation = bestEl.compareDocumentPosition(el);
    const elFollowsBest = (relation & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
    if (!elFollowsBest) {
      bestEl = el;
      bestPath = path;
    }
  }

  return bestPath;
}

/**
 * Looks up a Mantine-wired input by its form-field path.
 *
 * `getInputProps(path)` stamps `data-path` on the input element. We escape
 * the path for use in an attribute selector so paths containing brackets,
 * dots, or quotes (e.g. `addresses[0].street`) still match.
 */
function querySelectorByPath(path: string): Element | null {
  if (typeof document === 'undefined') return null;
  // Use CSS attribute-equals matching with a quoted value so the path can
  // contain special characters without breaking the selector.
  const escaped = path.replace(/(["\\])/g, '\\$1');
  return document.querySelector(`[${DATA_PATH_ATTR}="${escaped}"]`);
}

/**
 * Returns true when the element can sensibly receive focus. Hidden / disabled
 * / readOnly inputs are excluded so we don't trap the user on a field they
 * can't interact with.
 */
function isFocusable(el: HTMLElement): boolean {
  if (el.hidden) return false;

  // Bail on disabled / readOnly form controls. We check via property access
  // rather than attribute presence so the values reflect the live React state.
  const formEl = el as HTMLInputElement;
  if (formEl.disabled === true) return false;
  if (formEl.readOnly === true) return false;

  return true;
}

/**
 * Updates the shared aria-live region with the count of errored fields so
 * screen readers announce "N field(s) need attention." The region is
 * mounted once at the app root (see App.tsx). If the region isn't present
 * (e.g. in a unit-test render without the App shell), this is a no-op.
 */
function announce(count: number): void {
  if (typeof document === 'undefined') return;
  const region = document.getElementById(LIVE_REGION_ID);
  if (!region) return;

  const message = count === 1 ? '1 field needs attention.' : `${count} fields need attention.`;

  // Briefly clear the region first so the same message announced twice in a
  // row (e.g. user clicks Save twice) still triggers a fresh announcement.
  region.textContent = '';
  // Force a microtask boundary so the cleared text is flushed before the
  // new message; otherwise some screen readers coalesce the updates.
  setTimeout(() => {
    region.textContent = message;
  }, 0);
}
